import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import Icon from './Icon'

interface EditableFieldProps {
  value: string
  placeholder: string
  multiline?: boolean
  className?: string
  ariaLabel: string
  editLabel: string
  saveLabel: string
  onSave: (next: string) => void
}

const EditableField = ({
  value,
  placeholder,
  multiline,
  className,
  ariaLabel,
  editLabel,
  saveLabel,
  onSave,
}: EditableFieldProps) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Auto-focus + select on edit start
  useEffect(() => {
    if (!editing) return
    const target = multiline ? textareaRef.current : inputRef.current
    if (target) {
      target.focus()
      target.select()
    }
  }, [editing, multiline])

  const commit = () => {
    const next = draft.trim()
    if (next !== value) onSave(next)
    setEditing(false)
  }
  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }
  const startEditing = () => {
    setDraft(value)
    setEditing(true)
  }

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      commit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const handlePencilClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (editing) commit()
    else startEditing()
  }

  const isEmpty = value.length === 0
  const wrapperClass = `mp-field${className ? ` ${className}` : ''}${editing ? ' editing' : ''}`

  return (
    <div className={wrapperClass}>
      {editing ? (
        multiline ? (
          <textarea
            ref={textareaRef}
            className="mp-field-text"
            value={draft}
            rows={2}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <input
            ref={inputRef}
            className="mp-field-text"
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        )
      ) : (
        <span
          className="mp-field-text mp-field-static"
          data-empty={isEmpty || undefined}
          onClick={(e) => {
            e.stopPropagation()
            startEditing()
          }}
        >
          {isEmpty ? placeholder : value}
        </span>
      )}
      <button
        type="button"
        className="mp-pencil"
        // While editing, suppress the mousedown's default focus shift so the input's
        // onBlur doesn't fire before the click handler — otherwise blur commits and
        // the click would re-open edit mode.
        onMouseDown={(e) => {
          if (editing) e.preventDefault()
        }}
        onClick={handlePencilClick}
        aria-label={editing ? saveLabel : `${editLabel} ${ariaLabel}`}
        title={editing ? saveLabel : editLabel}
      >
        <Icon name={editing ? 'check' : 'pencil'} size={13} />
      </button>
    </div>
  )
}

export default EditableField
